import { useMemo, useState } from 'react';
import { useImportProspects } from '../../hooks/useProspects';
import {
  analyserImport,
  detectFormat,
  formaterResumeImport,
  parseContent,
  sembleCp1252
} from '../../utils/prospectImport';

/**
 * Modale d'import (§5.3).
 *
 * **Rien n'est écrit avant le clic sur « Importer ».** L'aperçu est produit par les fonctions pures
 * de la vague A : c'est exactement le même code qui tourne au moment de l'écriture, donc le
 * récapitulatif ne peut pas mentir.
 */

const ImportProspectsModal = ({ existingById = {}, onFermer }) => {
  const [contenu, setContenu] = useState('');
  const [nomFichier, setNomFichier] = useState('');
  const [erreurLecture, setErreurLecture] = useState('');
  const [ignoreExisting, setIgnoreExisting] = useState(false);
  const [rejetsOuverts, setRejetsOuverts] = useState(false);

  const { importer, enCours, resultat, erreur } = useImportProspects();

  const format = contenu ? detectFormat(contenu) : null;
  const encodageSuspect = contenu ? sembleCp1252(contenu) : false;

  const analyse = useMemo(() => {
    if (!contenu) return null;
    try {
      const parse = parseContent(contenu);
      if (!parse.format) {
        return { erreur: 'Format non reconnu : attendu du JSON, du JSONL ou du CSV.' };
      }
      const resultatAnalyse = analyserImport(parse.items, { existingById, ignoreExisting });
      return { ...resultatAnalyse, format: parse.format, erreursParsing: parse.erreurs };
    } catch (error) {
      return { erreur: `Fichier illisible : ${error.message}` };
    }
  }, [contenu, existingById, ignoreExisting]);

  const lireFichier = async (fichier) => {
    if (!fichier) return;
    setErreurLecture('');
    setNomFichier(fichier.name);
    try {
      const texte = await fichier.text();
      setContenu(texte);
    } catch (error) {
      setErreurLecture(`Lecture impossible : ${error.message}`);
    }
  };

  const surDepot = (evenement) => {
    evenement.preventDefault();
    lireFichier(evenement.dataTransfer?.files?.[0]);
  };

  const lancerImport = async () => {
    if (!analyse || !analyse.documents) return;
    await importer({
      documents: analyse.documents,
      resume: { ...analyse.resume, fichier: nomFichier, format: analyse.format }
    });
  };

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-4xl">
        <h3 className="font-bold text-lg mb-1">Importer un fichier Apify</h3>
        <p className="text-sm text-base-content/60 mb-4">
          JSON (recommandé), JSONL ou CSV. Aucune écriture avant le clic sur « Importer ».
        </p>

        <div
          className="border-2 border-dashed border-base-300 rounded-lg p-6 text-center mb-4"
          onDragOver={(evenement) => evenement.preventDefault()}
          onDrop={surDepot}
        >
          <input
            type="file"
            accept=".json,.jsonl,.csv"
            className="file-input file-input-bordered file-input-sm w-full max-w-md"
            onChange={(evenement) => lireFichier(evenement.target.files?.[0])}
          />
          <p className="text-xs text-base-content/50 mt-2">
            ou glissez-déposez le fichier dans cette zone
          </p>
          {nomFichier && (
            <p className="text-sm mt-2">
              <span className="font-semibold">{nomFichier}</span>
              {format ? ` · format détecté : ${format}` : ''}
            </p>
          )}
        </div>

        {encodageSuspect && (
          <div className="alert alert-warning text-sm mb-4">
            <span>
              Le fichier semble encodé en CP1252 (export Excel Windows) : les accents seront
              cassés. Réenregistrez-le en UTF-8 avant l’import.
            </span>
          </div>
        )}
        {erreurLecture && (
          <div className="alert alert-error text-sm mb-4">
            <span>{erreurLecture}</span>
          </div>
        )}
        {analyse?.erreur && (
          <div className="alert alert-error text-sm mb-4">
            <span>{analyse.erreur}</span>
          </div>
        )}
        {/* APERCU */}
        <label className="label cursor-pointer justify-start gap-2 mb-3">
          <input
            type="checkbox"
            className="checkbox checkbox-sm"
            checked={ignoreExisting}
            onChange={(evenement) => setIgnoreExisting(evenement.target.checked)}
          />
          <span className="label-text">Ignorer les fiches déjà en base (import additif)</span>
        </label>

        {analyse?.resume && (
          <div className="bg-base-200 rounded-lg p-3 mb-3">
            <p className="font-semibold text-sm">{formaterResumeImport(analyse.resume)}</p>
            <p className="text-xs text-base-content/60 mt-1">
              {analyse.resume.appelables} appelables · {analyse.resume.horsCible} hors cible ·{' '}
              {analyse.resume.avecServices} avec services suggérés · {analyse.resume.avecResume} avec
              résumé
              {analyse.erreursParsing > 0 && ` · ${analyse.erreursParsing} ligne(s) illisible(s)`}
            </p>
          </div>
        )}

        {analyse?.rejets?.length > 0 && (
          <div className="mb-3">
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={() => setRejetsOuverts((ouvert) => !ouvert)}
            >
              {rejetsOuverts ? '▾' : '▸'} {analyse.rejets.length} fiche(s) rejetée(s)
            </button>
            {rejetsOuverts && (
              <ul className="text-xs text-base-content/70 mt-2 space-y-1 max-h-40 overflow-y-auto">
                {analyse.rejets.map((rejet) => (
                  <li key={`${rejet.ligne}-${rejet.motif}`}>
                    Ligne {rejet.ligne} · {rejet.nom || rejet.placeId || 'sans identité'} —{' '}
                    {rejet.libelle}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {resultat && (
          <div className="alert alert-success text-sm mb-3">
            <span>
              {resultat.ecrits} document{resultat.ecrits > 1 ? 's' : ''} écrit
              {resultat.ecrits > 1 ? 's' : ''}. Réimporter le même fichier ne créera aucun doublon.
            </span>
          </div>
        )}
        {erreur && (
          <div className="alert alert-error text-sm mb-3">
            <span>
              {erreur.code === 'permission-denied'
                ? 'Accès refusé : déployez la règle `prospects` (firebase deploy --only firestore:rules).'
                : erreur.message}
            </span>
          </div>
        )}

        <div className="modal-action">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onFermer}>
            {resultat ? 'Fermer' : 'Annuler'}
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!analyse?.documents || analyse.documents.length === 0 || enCours}
            onClick={lancerImport}
          >
            {enCours ? 'Import en cours…' : 'Importer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportProspectsModal;
